import React, { useCallback, useState, useRef, useEffect } from 'react';
import cls from 'js/utils/classNameBuilder.util';
import { Button } from 'js/components/common/Button';
import { useFormatter } from 'js/hooks/useFormatter';

import { InputShallow } from '../../Input';
import { formatCurrency } from '../helpers';
import controlStyle from '../../Control/Control.scss';
import ValidationIcon from '../../Icons/ValidationIcon';

import style from './index.scss';

export default ({
    /* rff render props */
    input,
    meta,

    /* user provided props */
    tt,
    type = 'text',
    newLimit,
    currency: currCode,
    inputMode = 'decimal',
    classNames = {},
    resetDate,
    timePeriod,
    currentLimit,
    currencyCode,
    remainingLimit,
    newLimitStartDate,

    unlimitedValue,
    precision = 0,

    ...inputDomProps
}) => {
    const formatter = useFormatter();
    const { value, onChange } = input;
    const { active, touched, error, submitting } = meta;
    const currency = formatCurrency({ formatter, tt, currencyCode, precision, unlimitedValue });

    const [lastValue, setLastValue] = useState('');
    const [isFocusOnInputNeeded, setIsFocusOnInputNeeded] = useState(false);
    const isUnlimited = value === unlimitedValue;
    const inputRef = useRef();

    const toggleUnlimited = useCallback(
        () => {
            if (isUnlimited) {
                onChange(lastValue);
                setLastValue('');
                setIsFocusOnInputNeeded(true);
            } else {
                setLastValue(value);
                onChange(unlimitedValue);
            }
        },
        [isUnlimited, onChange, value, lastValue, unlimitedValue]
    );

    useEffect(() => {
        if (!inputRef.current?.disabled && isFocusOnInputNeeded) {
            inputRef.current?.focus();
            setIsFocusOnInputNeeded(false);
        }
    }, [isFocusOnInputNeeded]);

    const currentLimitTitle = tt(`current.${timePeriod}`, [currency(currentLimit)]);
    const makeUnlimitedTitle = isUnlimited ? tt('makeLimited') : tt('makeUnlimited');
    const btnStyle = isUnlimited ? style.makeLimited : style.makeUnlimited;
    const btnView = isUnlimited ? 'primary' : 'secondary';

    return (
        <div className={cls(['editor'], style, classNames)}>
            <div className={style.label}>{currentLimitTitle}</div>
            <div className={style.inputs}>
                <div className={`${style.inputLeft} ${controlStyle.content}`}>
                    <span className={style.caption}>{tt('setNewLimit')}</span>
                    <div className={style.inputHolder} currency={formatter.getCurrencySymbol(currCode)}>
                        <InputShallow
                            ref={inputRef}
                            type={type}
                            inputMode={inputMode}
                            {...input}
                            {...inputDomProps}
                            aria-label={tt('setNewLimit')}
                            {...isUnlimited ? { disabled: true, value: tt('unlimited') } : {}}
                            {...submitting ? { disabled: true } : {}}
                        />
                        {touched && !active && <ValidationIcon
                            animate={true}
                            isValid={!error}
                            aria-label={error}
                            tabIndex={error ? 0 : -1}
                            className={style.validationIcon}
                        />}
                    </div>
                </div>
                <div className={style.unlimitedBox}>
                    <span className={style.captionUnlimited}>{makeUnlimitedTitle}</span>
                    <Button
                        view={btnView}
                        size="small"
                        className={btnStyle}
                        onClick={toggleUnlimited}
                    >
                        <span className={style.icon} />
                    </Button>
                </div>
            </div>
        </div>
    );
};
